import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

export interface TooltipProps {
  children: ReactNode | string | JSX.Element;
  button: ReactNode | string | JSX.Element;
  disabled?: boolean;
  position?: 'center' | 'down' | 'up' | null;
  positionHorizontal?: 'center' | 'left' | 'right';
  variant?: 'black' | 'gray';
  className?: string;
  classNameTooltip?: string;
  delayTimeClose?: number;
  delayTimeOpen?: number;
  relativeBlock?: boolean;
}

export const Tooltip = ({
  children,
  button,
  disabled = false,
  position = 'down',
  positionHorizontal = 'center',
  variant = 'black',
  className,
  classNameTooltip,
  delayTimeClose = 1000,
  delayTimeOpen = 500,
  relativeBlock = true,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [id] = useState(Math.round(Math.random() * 1000));
  const handlerRef = useRef<any>(null);

  const handleOnMouseEnter = () => {
    setOnMouseEnter(true);
    const tooltips = document.querySelectorAll('[id^="tooltip-delay-"]');
    tooltips.forEach(tooltip => {
      if (tooltip.id === 'tooltip-delay-' + id) return;
      if (handlerRef.current) {
        clearTimeout(handlerRef.current);
      }
      tooltip.classList.add('z-0', 'w-0', 'h-0', 'p-0', 'opacity-0', 'overflow-hidden');
      tooltip.classList.remove('z-10', 'w-10/12', 'min-w-[200px]', 'py-2', 'px-1', 'opacity-100');
    });
  };

  const handleOnMouseLeave = () => {
    setOnMouseEnter(false);
    handlerRef.current = setTimeout(() => setIsOpen(false), delayTimeClose);
  };

  useEffect(() => {
    const handler = setTimeout(
      () =>
        setOnMouseEnter(prev => {
          if (prev) {
            setIsOpen(true);
          }
          return prev;
        }),
      delayTimeOpen,
    );
    return () => clearTimeout(handler);
  }, [onMouseEnter, delayTimeOpen]);

  if (disabled) {
    return <>{button}</>;
  }

  return (
    <div className={classNames({ relative: relativeBlock }, className)}>
      <div onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
        {button}
      </div>

      <div
        id={`tooltip${delayTimeClose > 0 ? '-delay' : ''}-${id}`}
        className={classNames(
          'absolute bg-opacity-100 mx-auto rounded-lg text-center duration-500 transition-opacity',
          {
            'inset-x-0': positionHorizontal === 'center',
            'right-5': positionHorizontal === 'left',
            'left-0': positionHorizontal === 'right',
            'bg-black text-white': variant === 'black',
            'bg-gray-100 text-gray-500': variant === 'gray',
            'bottom-[34px]': position === 'up',
            'top-[34px]': position === 'down',
            'bottom-[-28px]': position === 'center',
            'z-0 w-0 p-0 opacity-0 overflow-hidden': !isOpen,
            'z-10 w-10/12 min-w-[200px] py-2 px-1 opacity-100': isOpen,
            [classNameTooltip || '']: isOpen,
          },
        )}
      >
        {children}
      </div>
    </div>
  );
};
